import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { MdArrowBack, MdTrendingUp, MdTrendingDown, MdInfoOutline, MdShowChart, MdVisibility, MdVisibilityOff } from 'react-icons/md';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip,
  PieChart, Pie, Cell,
} from 'recharts';
import { authHeaders } from '../services/backendAPI';
import { useProfile } from '../contexts/ProfileContext';
import { useApp, maskAmount } from '../contexts/AppContext';
import { computeInvestmentData } from '../utils/computeInvestmentData';

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmt  = v => '$' + Math.abs(Number(v)).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// Hero-only formatter — drops cents from the headline portfolio total to
// match the rest of the app. Smaller values inline still use fmt().
const fmtHero = v => '$' + Math.round(Math.abs(Number(v))).toLocaleString('en-CA');
const fmtK = v => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`;

const RANGE_OPTIONS = ['1M', '3M', '6M', '1Y', 'All'];

// Months to keep per range (applied to profile transaction chart data)
const RANGE_MONTHS = { '1M': 1, '3M': 3, '6M': 6, '1Y': 12, 'All': Infinity };

// High-net-worth palette: gold + premium dark green + warm earthy tones.
// Avoids the bright shield-green to keep the wealth-management aesthetic.
// Hex literals because Recharts SVG fill can't resolve CSS variables.
const ACCT_COLORS = {
  rrsp:               '#CFBD91', // td-gold     (primary retirement vehicle)
  tfsa:               '#002B1A', // td-premium  (tax-free wealth)
  fhsa:               '#7C5636', // td-brown    (housing, earthy)
  resp:               '#D9BFAF', // td-tan      (soft, education)
  investment_account: '#946E9F', // td-lavender (distinctive non-reg)
};

// ── Mock fallback data (used when no profile is loaded) ───────────────────────

const MOCK_HOLDINGS = [
  { ticker: 'VFV.TO',  name: 'Vanguard S&P 500 ETF',   value: 28420, shares: 112.4, gainPct: null },
  { ticker: 'XEQT.TO', name: 'iShares Core Equity ETF', value: 22180, shares: 298.1, gainPct: null },
  { ticker: 'TD.TO',   name: 'TD Bank',                  value: 18650, shares: 240.0, gainPct: null },
];

const MOCK_ALLOC = [
  { name: 'Canadian Equity', value: 42, color: '#CFBD91' }, // td-gold    (lead)
  { name: 'US Equity',       value: 31, color: '#002B1A' }, // td-premium (deep)
  { name: 'Fixed Income',    value: 18, color: '#7C5636' }, // td-brown   (warm)
  { name: 'Cash',            value:  9, color: '#D9BFAF' }, // td-tan     (soft)
];

function buildMockPerfData(months) {
  const start = 108_000, end = 133_950;
  const labels = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return Array.from({ length: months }, (_, i) => {
    const t = i / (months - 1);
    const noise = Math.sin(i * 2.4) * 1800 + Math.cos(i * 1.1) * 900;
    return { label: labels[i % 12], value: Math.round(start + (end - start) * t + noise) };
  });
}

const MOCK_PERF = {
  '1M': buildMockPerfData(30).map((d, i) => ({ ...d, label: String(i + 1) })),
  '3M': buildMockPerfData(13),
  '6M': buildMockPerfData(13),
  '1Y': buildMockPerfData(12),
  'All': buildMockPerfData(24),
};

// ── Sub-components ────────────────────────────────────────────────────────────

// When privacy mode is on, swap dollar values for "% of portfolio" — this
// preserves the relative breakdown info without exposing absolute amounts.
const pctOf = (value, total) =>
  total > 0 ? `${((value / total) * 100).toFixed(1)}%` : '—';

const AccountCard = ({ id, label, color, value, room, roomLabel, strategy, hideBalances, totalValue }) => (
  <div className="port-acct-card" style={{ borderTopColor: color || ACCT_COLORS[id] || 'var(--td-green)' }}>
    <div className="port-acct-label" style={{ color: color || ACCT_COLORS[id] || 'var(--td-green)' }}>{label}</div>
    <div className="port-acct-value">
      {hideBalances ? pctOf(value, totalValue) : fmt(value)}
    </div>
    {room != null && !hideBalances && (
      <div className="port-acct-room">
        <span className="port-acct-room-label">{roomLabel}</span>
        <span className="port-acct-room-val">{fmt(room)}</span>
      </div>
    )}
    {strategy && (
      <div className="port-acct-strategy">{strategy}</div>
    )}
  </div>
);

const HoldingRow = ({ ticker, name, value, shares, gainPct, livePrice, hideBalances, totalValue }) => {
  const displayGain = gainPct;
  const up = displayGain != null && displayGain >= 0;
  return (
    <div className="port-holding-row">
      <div className="port-holding-ticker-wrap">
        <div className="port-holding-ticker">{ticker}</div>
        <div className="port-holding-name">{name}</div>
      </div>
      <div className="port-holding-right">
        {hideBalances
          ? <div className="port-holding-value">{pctOf(value, totalValue)}</div>
          : (livePrice != null
              ? <div className="port-holding-value">{fmt(livePrice)} <span style={{ fontSize: 10, color: 'var(--text-light)' }}>live</span></div>
              : <div className="port-holding-value">{fmt(value)}</div>)
        }
        {displayGain != null && (
          <div className={`port-holding-gain ${up ? 'gain-up' : 'gain-down'}`}>
            {up ? <MdTrendingUp size={13} /> : <MdTrendingDown size={13} />}
            {Math.abs(displayGain).toFixed(1)}%
          </div>
        )}
      </div>
    </div>
  );
};

// ── Tooltip formatter ─────────────────────────────────────────────────────────

const TipStyle = {
  background: 'var(--bg-card)',
  border: '1px solid var(--border-color)',
  borderRadius: 6,
  fontSize: 11,
  color: 'var(--text-dark)',
};

// ── Main screen ───────────────────────────────────────────────────────────────

const MOBILE_MQ = typeof window !== 'undefined' && window.matchMedia('(max-width: 600px)');

const PortfolioScreen = ({ onNavigate, onBack }) => {
  const [range, setRange]             = useState('1Y');
  const [chartMode, setChartMode]     = useState('portfolio'); // 'portfolio' | 'market'
  const [marketData, setMarketData]   = useState(null);
  const [marketLoading, setMarketLoading] = useState(false);
  const [marketError, setMarketError] = useState(null);
  const [liveQuotes, setLiveQuotes]   = useState({}); // ticker -> { price, previousClose }
  const [chartHeight, setChartHeight] = useState(() => MOBILE_MQ?.matches ? 240 : 160);

  const { loadedProfileData } = useProfile();
  const { hideBalances, setHideBalances } = useApp();

  React.useEffect(() => {
    if (!MOBILE_MQ) return;
    const h = e => setChartHeight(e.matches ? 240 : 160);
    MOBILE_MQ.addEventListener('change', h);
    return () => MOBILE_MQ.removeEventListener('change', h);
  }, []);

  const profile    = loadedProfileData?.profile;
  const investData = useMemo(() => computeInvestmentData(profile), [profile]);

  // No mock fallback for investments anymore — if the loaded profile (or the
  // none-loaded default) doesn't have a recognized investment account, we
  // show a clean empty state instead of inventing a fake $134k portfolio.
  const hasInvestments = !!investData;
  const accounts   = investData?.accounts   ?? [];
  const totalValue = investData?.totalValue ?? 0;

  // All tickers across all accounts (only from profiles that have explicit holdings)
  const allTickers = useMemo(() => {
    const tickers = [];
    accounts.forEach(a => {
      if (Array.isArray(a.holdings)) {
        a.holdings.forEach(h => { if (h.ticker) tickers.push(h.ticker); });
      }
    });
    return tickers;
  }, [accounts]);

  // Also grab tickers from mock holdings when no profile loaded
  const holdingsToShow = useMemo(() => {
    if (!investData) return MOCK_HOLDINGS;
    // Build flat list from all accounts' holdings arrays
    const list = [];
    accounts.forEach(a => {
      if (Array.isArray(a.holdings) && a.holdings.length) {
        a.holdings.forEach(h => list.push({ ...h, accountId: a.id }));
      }
    });
    return list;
  }, [investData, accounts]);

  // Fetch live quotes whenever tickers available
  useEffect(() => {
    if (!allTickers.length) return;
    const fetchQuotes = async () => {
      try {
        const r = await fetch(`/api/stocks/quote?tickers=${allTickers.join(',')}`, {
          headers: authHeaders(),
          signal: AbortSignal.timeout(10000),
        });
        if (!r.ok) return;
        const data = await r.json();
        const map = {};
        (data.quotes ?? []).forEach(q => { if (!q.error) map[q.ticker] = q; });
        setLiveQuotes(map);
      } catch { /* silent */ }
    };
    fetchQuotes();
  }, [allTickers]);

  // ── Portfolio chart data (profile transaction history filtered by range) ─────

  const portfolioChartData = useMemo(() => {
    if (investData?.chartData?.length) {
      const cutoff = RANGE_MONTHS[range] ?? 12;
      return cutoff === Infinity
        ? investData.chartData
        : investData.chartData.slice(-cutoff);
    }
    return MOCK_PERF[range] ?? MOCK_PERF['1Y'];
  }, [investData, range]);

  // ── Live market benchmark data ────────────────────────────────────────────────

  const fetchMarketData = useCallback(async () => {
    setMarketLoading(true);
    setMarketError(null);
    try {
      const r = await fetch(`/api/stocks/history?ticker=XIU.TO&range=${range}`, {
        headers: authHeaders(),
        signal: AbortSignal.timeout(12000),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const json = await r.json();
      // Normalize prices to start at the same value as portfolio total
      const raw = json.data ?? [];
      if (!raw.length) throw new Error('No market data returned');
      const basePrice = raw[0].close;
      const basePort  = portfolioChartData[0]?.value ?? totalValue;
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      const normalized = raw.map(d => ({
        label: months[new Date(d.date).getMonth()],
        value: Math.round((d.close / basePrice) * basePort),
        close: d.close,
      }));
      setMarketData(normalized);
    } catch (err) {
      setMarketError('Could not load market data');
      setMarketData(null);
    } finally {
      setMarketLoading(false);
    }
  }, [range, portfolioChartData, totalValue]);

  useEffect(() => {
    if (chartMode === 'market') {
      fetchMarketData();
    }
  }, [chartMode, range, fetchMarketData]);

  const activeChartData = chartMode === 'market' && marketData ? marketData : portfolioChartData;
  const minVal = Math.min(...activeChartData.map(d => d.value));
  const maxVal = Math.max(...activeChartData.map(d => d.value));

  // Recharts' ResponsiveContainer measures its parent on mount. If this screen
  // mounts mid tab-transition — or the chart only enters the DOM once profile
  // data lands (hasInvestments flips false → true) — that first measurement can
  // be 0px wide and the chart paints blank until a later interaction (tapping a
  // range button) forces a re-measure. Dispatch a resize once the chart's
  // inputs settle so it always draws without needing a tap.
  useEffect(() => {
    if (!hasInvestments) return undefined;
    const raf = requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    return () => cancelAnimationFrame(raf);
  }, [hasInvestments, range, chartMode, activeChartData.length]);

  // ── Asset allocation (derive from accounts if possible) ──────────────────────

  const allocData = useMemo(() => {
    if (!investData) return MOCK_ALLOC;
    // Simple allocation by account type
    const total = totalValue;
    if (!total) return MOCK_ALLOC;
    return accounts.map(a => ({
      name: a.label,
      value: Math.round((a.value / total) * 100),
      color: a.color,
    }));
  }, [investData, accounts, totalValue]);

  return (
    <div className="screen port-screen bh-flow">
      <header className="bh-flow-hero dark tall">
        <div className="bh-flow-hero-row">
          <div className="bh-flow-hero-left">
            <button className="bh-flow-back" onClick={onBack || (() => onNavigate('accounts'))} aria-label="Back to accounts">
              <MdArrowBack />
            </button>
            <h1 className="bh-flow-title">Investing</h1>
          </div>
          {/* Privacy toggle — same global flag as the home top bar, so
              flipping it here syncs across every screen. The eye sits in
              the dark hero so the gold/white iconography stays consistent. */}
          <button
            type="button"
            onClick={() => setHideBalances(v => !v)}
            aria-label={hideBalances ? 'Show balances' : 'Hide balances'}
            aria-pressed={hideBalances}
            title={hideBalances ? 'Show balances' : 'Hide balances'}
            style={{
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.16)',
              color: 'var(--td-gold)',
              width: 36, height: 36,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', flexShrink: 0,
              borderRadius: 'var(--radius-md)',
            }}
          >
            {hideBalances ? <MdVisibilityOff size={20} /> : <MdVisibility size={20} />}
          </button>
        </div>

        <div>
          <p style={{ margin: '0 0 var(--space-3xs)', font: 'var(--bh-overline)', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--td-gold)', opacity: 0.85 }}>
            Portfolio Value
          </p>
          <p style={{ margin: 0, font: 'var(--bh-display)', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--bh-white)' }}>
            {maskAmount(fmtHero(totalValue), hideBalances)}
          </p>
          {loadedProfileData && (
            <p style={{ margin: 'var(--space-3xs) 0 0', font: 'var(--bh-body-sm)', color: 'var(--td-gold)', opacity: 0.7 }}>
              {loadedProfileData.name}
            </p>
          )}
          {!loadedProfileData && (
            <p style={{ margin: 'var(--space-3xs) 0 0', font: 'var(--bh-caption)', color: 'var(--td-tan)', opacity: 0.7 }}>
              Demo data — load a profile to see real values
            </p>
          )}
        </div>
      </header>

      <div className="scroll-content gray-bg">

        {!hasInvestments ? (
          <div style={{ padding: 'var(--space-lg) var(--space-md)', textAlign: 'center', color: 'var(--text-medium)' }}>
            <div style={{ font: 'var(--bh-h3)', color: 'var(--text-dark)', marginBottom: 'var(--space-2xs)' }}>
              No investments yet
            </div>
            <p style={{ margin: 0, font: 'var(--bh-body-sm)', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
              {(() => {
                const n = loadedProfileData?.name;
                return n && n !== 'Default' ? n.split(' ')[0] : 'This profile';
              })()} doesn't have a registered investment account yet.
              Opening one (TFSA, RRSP, or self-directed) is a great first step.
            </p>
            <button
              className="bh-btn-gold"
              style={{ marginTop: 'var(--space-md)', maxWidth: 280 }}
              onClick={() => onNavigate && onNavigate('openAccount')}
            >
              Open an investment account
            </button>
          </div>
        ) : (
        <>
        {/* Buy / Sell action row — high-net-worth: gold buy + dark outline sell */}
        <div style={{ padding: 'var(--space-md) var(--space-sm) 0', display: 'flex', gap: 'var(--space-2xs)' }}>
          <button className="bh-btn-gold" style={{ flex: 1 }}>Buy</button>
          <button className="bh-btn-outline-dark" style={{ flex: 1 }}>Sell</button>
        </div>

        {/* ── Performance chart ── */}
        <div className="viz-chart-card" style={{ marginTop: 0, borderRadius: '0 0 10px 10px' }}>

          {/* Range selector + Market toggle */}
          <div className="port-range-row" style={{ flexWrap: 'wrap', gap: 4 }}>
            {RANGE_OPTIONS.map(r => (
              <button
                key={r}
                className={`port-range-btn${range === r ? ' active' : ''}`}
                onClick={() => setRange(r)}
              >{r}</button>
            ))}
            <button
              className={`port-range-btn port-market-btn${chartMode === 'market' ? ' active' : ''}`}
              style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 3 }}
              onClick={() => setChartMode(m => m === 'market' ? 'portfolio' : 'market')}
            >
              <MdShowChart size={13} />
              {chartMode === 'market' ? 'Market (XIU.TO)' : 'vs Market'}
            </button>
          </div>

          {marketLoading && (
            <div style={{ textAlign: 'center', padding: '12px 0', fontSize: 12, color: 'var(--text-light)' }}>
              Loading market data…
            </div>
          )}
          {marketError && (
            <div style={{ textAlign: 'center', padding: '4px 0', fontSize: 11, color: 'var(--td-red)' }}>
              {marketError}
            </div>
          )}

          <ResponsiveContainer width="100%" height={chartHeight}>
            <AreaChart data={activeChartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="portGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#CFBD91" stopOpacity={0.40} />
                  <stop offset="95%" stopColor="#CFBD91" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="label"
                tick={{ fontSize: 9, fill: 'var(--text-medium)' }}
                axisLine={false} tickLine={false}
                interval="preserveStartEnd"
              />
              <YAxis
                domain={[minVal * 0.985, maxVal * 1.01]}
                tick={{ fontSize: 9, fill: 'var(--text-medium)' }}
                axisLine={false} tickLine={false}
                tickFormatter={hideBalances ? () => '' : fmtK}
                width={hideBalances ? 0 : 38}
              />
              <Tooltip
                formatter={(v) => [
                  hideBalances
                    ? (activeChartData[0]?.value
                        ? `${((v / activeChartData[0].value - 1) * 100).toFixed(1)}%`
                        : '—')
                    : fmt(v),
                  chartMode === 'market' ? 'XIU.TO (normalized)' : 'Portfolio',
                ]}
                contentStyle={TipStyle}
                cursor={{ stroke: '#CFBD91', strokeWidth: 1, strokeDasharray: '3 3' }}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke="#CFBD91"
                strokeWidth={2}
                fill="url(#portGrad)"
                dot={false}
                activeDot={{ r: 4, fill: '#CFBD91', stroke: 'var(--bg-card)', strokeWidth: 2 }}
              />
            </AreaChart>
          </ResponsiveContainer>

          {chartMode === 'market' && !marketLoading && !marketError && (
            <div style={{ fontSize: 10, color: 'var(--text-light)', textAlign: 'right', paddingTop: 2 }}>
              XIU.TO (iShares S&P/TSX 60 ETF) normalized to portfolio starting value
            </div>
          )}
        </div>

        {/* ── Account breakdown cards ── */}
        <div className="port-section-label-row">
          <span className="port-section-label" style={{ padding: 0 }}>Accounts</span>
        </div>
        <div className="port-acct-scroll">
          {accounts.map(a => <AccountCard key={a.id} {...a} hideBalances={hideBalances} totalValue={totalValue} />)}
        </div>

        {/* ── Holdings or strategy ── */}
        {holdingsToShow.length > 0 ? (
          <>
            <div className="port-section-label">Holdings</div>
            <div className="white-container" style={{ padding: 0 }}>
              {holdingsToShow.map(h => (
                <HoldingRow
                  key={h.ticker}
                  {...h}
                  livePrice={liveQuotes[h.ticker]?.price ?? null}
                  gainPct={
                    liveQuotes[h.ticker]
                      ? ((liveQuotes[h.ticker].price - liveQuotes[h.ticker].previousClose) / liveQuotes[h.ticker].previousClose * 100)
                      : (h.gainPct ?? null)
                  }
                  hideBalances={hideBalances}
                  totalValue={totalValue}
                />
              ))}
            </div>
          </>
        ) : (
          <>
            {/* Show strategy text for accounts that have it */}
            {accounts.some(a => a.holdingsSummary || a.strategy) && (
              <>
                <div className="port-section-label">Portfolio Strategy</div>
                <div className="white-container">
                  {accounts.map(a => (a.holdingsSummary || a.strategy) && (
                    <div key={a.id} style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: a.color, marginBottom: 3 }}>{a.label}</div>
                      {a.strategy && <div style={{ fontSize: 12, color: 'var(--text-dark)' }}>{a.strategy}</div>}
                      {a.holdingsSummary && <div style={{ fontSize: 11, color: 'var(--text-medium)', marginTop: 2 }}>{a.holdingsSummary}</div>}
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {/* ── Asset allocation donut ── */}
        <div className="viz-chart-card">
          <div className="viz-chart-title">Account Allocation</div>
          <div className="viz-chart-sub">
            {investData ? 'Across your investment accounts' : 'Across all registered & non-registered accounts'}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ResponsiveContainer width={130} height={130}>
              <PieChart>
                <Pie
                  data={allocData}
                  dataKey="value"
                  cx="50%" cy="50%"
                  innerRadius={46} outerRadius={58}
                  stroke="#000000" strokeWidth={1}
                >
                  {allocData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 7 }}>
              {allocData.map(a => (
                <div key={a.name} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 8, height: 8, borderRadius: 2, background: a.color, flexShrink: 0 }} />
                  <span style={{ fontSize: 11, color: 'var(--text-medium)', flex: 1 }}>{a.name}</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-dark)' }}>{a.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="viz-footer" style={{ margin: '0 16px 28px' }}>
          <span>TD Direct Investing · IVA Demo{allTickers.length ? ' · Live quotes from Yahoo Finance' : ' · Prices delayed 15 min'}</span>
        </div>
        </>
        )}

      </div>
    </div>
  );
};

export default PortfolioScreen;
